import clone from 'clone';
import { AJV_EXTENSION_PROPERTY_PATH, AJV_EXTENSION_REF_SCHEMA } from '../../globals';
import { determinePropertyPathFromId } from '../determinePropertyPathFromId';
import {
    ISchemaAnyOf,
    ISchemaArray,
    ISchemaObject,
    ISchemaObjectProperties,
    ISchemaOneOf,
    ISchemaRecord,
    ISchemaRefFormatted,
    ISchemaTuple,
} from '../properties/composite';
import { SchemaTypeName } from '../properties/primitives';
import { ISchemaCoreType, ISchemaType } from '../properties/union';

/**
 * Recursively remove $refs from schema
 */
export function removeRefs<T extends ISchemaType>(input_schema: T): T {
    const schema = clone(input_schema);

    return removeRefsFromSchema({
        schema,
        property_path: determinePropertyPathFromId({
            $id: (input_schema as { $id?: string }).$id,
        }),
    });
}

/**
 * Recursively remove $refs from schema
 */
function removeRefsFromSchema<T extends ISchemaType>({
    schema: input_schema,
    property_path,
}: {
    schema: T;
    property_path: string;
}): T {
    /**
     * When removing refs, we always overwrite [AJV_EXTENSION_PROPERTY_PATH]
     * - Don't want to maintain nested-$ref-derived [AJV_EXTENSION_PROPERTY_PATH] values
     */
    if (
        input_schema[AJV_EXTENSION_PROPERTY_PATH] &&
        property_path !== input_schema[AJV_EXTENSION_PROPERTY_PATH]
    ) {
        input_schema[AJV_EXTENSION_PROPERTY_PATH] = property_path;
    }

    /**
     * $ref: Shift schema properties – apart from $ref itself – into [AJV_EXTENSION_REF_SCHEMA]
     */
    const formatted_ref_schema = input_schema as ISchemaRefFormatted;
    if (formatted_ref_schema.$ref) {
        /**
         * $ref must have been built using Type.ref()
         */
        if (!formatted_ref_schema[AJV_EXTENSION_REF_SCHEMA]) {
            console.error(`Missing "${AJV_EXTENSION_REF_SCHEMA}" property:`, input_schema);

            throw new Error(
                `Cannot remove $ref from schema: $ref was not built with Type.ref(), and is missing "${AJV_EXTENSION_REF_SCHEMA}" property`
            );
        }

        /**
         * Omit duplicated "$ref" property from underlying schema
         */
        const {
            // @ts-expect-error: $ref field is duplicated under [AJV_EXTENSION_REF_SCHEMA]
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            $ref: underlying_schema_ref,
            ...underlying_schema
        } = formatted_ref_schema[AJV_EXTENSION_REF_SCHEMA];

        return removeRefsFromSchema({
            schema: underlying_schema as T,
            property_path,
        });
    }

    /**
     * Core type (primitive + nested)
     */
    const core_schema = input_schema as Extract<T, ISchemaCoreType>;
    if (core_schema.type) {
        return removeRefsFromNestedTypes({
            schema: core_schema,
            property_path,
        });
    }

    /**
     * Union/logical types
     */
    const any_of_schema = input_schema as ISchemaAnyOf<Extract<T, ISchemaCoreType>>;
    const one_of_schema = input_schema as ISchemaOneOf<Extract<T, ISchemaCoreType>>;

    if (any_of_schema.anyOf) {
        any_of_schema.anyOf = any_of_schema.anyOf.map((t, index) =>
            removeRefsFromSchema({ schema: t, property_path: `${property_path}/anyOf/${index}` })
        );
    } else if (one_of_schema.oneOf) {
        one_of_schema.oneOf = one_of_schema.oneOf.map((t, index) =>
            removeRefsFromSchema({ schema: t, property_path: `${property_path}/oneOf/${index}` })
        );
    }

    return input_schema;
}

/**
 * Remove $refs from nested object/record/array/tuple schemata
 * - Traverse tree down into nested properties
 */
function removeRefsFromNestedTypes<T extends ISchemaCoreType>({
    schema,
    property_path,
}: {
    schema: T;
    property_path: string;
}): T {
    //  array
    const array_schema = schema as ISchemaArray;
    if (array_schema.type === SchemaTypeName.array && array_schema.items) {
        array_schema.items = removeRefsFromSchema({
            schema: array_schema.items,
            property_path: `${property_path}/items`,
        });
    }

    //  tuple
    const tuple_schema = schema as ISchemaTuple;
    if (tuple_schema.type === SchemaTypeName.array && tuple_schema.prefixItems) {
        tuple_schema.prefixItems = tuple_schema.prefixItems.map((item, index) =>
            removeRefsFromSchema({
                schema: item,
                property_path: `${property_path}/prefixItems/${index}`,
            })
        );
    }

    //  object
    const object_schema = schema as ISchemaObject<any, string>;
    if (object_schema.type === SchemaTypeName.object && object_schema.properties) {
        object_schema.properties = removeRefsFromObjectProperties({
            properties: object_schema.properties,
            property_path_prefix: `${property_path}/properties`,
        });
    }

    //  record
    const record_schema = schema as ISchemaRecord<any>;
    if (record_schema.type === SchemaTypeName.object && record_schema.patternProperties) {
        record_schema.patternProperties = removeRefsFromObjectProperties({
            properties: record_schema.patternProperties,
            property_path_prefix: `${property_path}/patternProperties`,
        });
    }

    /**
     * @todo: If adding support for `additionalProperties: <schema>`, we'll need to format
     */

    return schema as T;
}

/**
 * Iterate through object properties
 */
function removeRefsFromObjectProperties<
    Properties extends ISchemaObjectProperties<Extract<keyof Properties, string>>
>({
    properties,
    property_path_prefix,
}: {
    properties: Properties;
    property_path_prefix: string;
}): Properties {
    for (const field in properties) {
        properties[field] = removeRefsFromSchema({
            schema: properties[field],
            property_path: `${property_path_prefix}/${field}`,
        });

        //  @todo: Handle `.not` (https://json-schema.org/understanding-json-schema/reference/combining.html#not)
    }

    return properties;
}
